/**
 * @file edit-route-order-modal.tsx
 * @module app/modules/orders-delivery/components/delivery-plan/edit-route-order-modal
 * @description Modal for manually editing a route-vehicle's assigned orders: order-type
 * filter, search, discarded-orders notification/table, selectable orders table, and save.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.2.1
 * @version 2.1.2
 */

import { Button, Col, Modal } from 'react-bootstrap';
import Loader from '../../../../../Global/loader';
import { KTSVG } from '../../../../../_admin/helpers';
import Notification from '../../../../notification/notification2';
import DiscardedOrdersTable from './discarded-orders-table';
import EditableOrdersTable from './editable-orders-table';
import {
  formatDurationSafe,
  formatWeightTons,
  getTotalTravelDuration,
  kgToTons,
  toSafeNumber,
} from '../../utils/delivery-plan-details-helpers';

interface EditRouteOrderModalProps {
  show: boolean;
  onClose: () => void;
  orderTypeFilter: string;
  onOrderTypeFilterChange: (value: string) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  loading: boolean;
  orders: any[];
  filteredOrders: any[];
  visible: boolean;
  discardedOrder: any[];
  selectedOrder: number;
  discarded: boolean;
  onToggleDiscarded: () => void;
  modalVehicleAnalytics: any;
  currentUser: any;
  selectLoading: boolean;
  selectedId: any[];
  totalRecords: number;
  onSelectAll: () => void;
  onChecked: (id: string) => void;
  onSave: () => void;
  onSaveLoader: boolean;
}

const EditRouteOrderModal = ({
  show,
  onClose,
  orderTypeFilter,
  onOrderTypeFilterChange,
  searchTerm,
  onSearchChange,
  loading,
  orders,
  filteredOrders,
  visible,
  discardedOrder,
  selectedOrder,
  discarded,
  onToggleDiscarded,
  modalVehicleAnalytics,
  currentUser,
  selectLoading,
  selectedId,
  totalRecords,
  onSelectAll,
  onChecked,
  onSave,
  onSaveLoader,
}: EditRouteOrderModalProps) => {
  return (
    <Modal
      show={show}
      backdrop="static"
      dialogClassName="modal-dialog-centered min-w-lg-1000px min-w-md-700px"
      className="border-r10px"
      contentClassName="p-5"
      centered
      size="xl"
    >
      <Modal.Header className="border-bottom-0 pb-0">
        <div className="w-100 d-flex align-items-center justify-content-between">
          <div className="flex-grow-1">
            <Modal.Title className="fs-26 fw-bolder">Edit Route Order</Modal.Title>
          </div>
          <div>
            <Col xs={12} md={6} lg={6}>
              <div className="d-flex align-items-center gap-2 justify-content-start justify-content-md-end">
                <div className="min-w-150px">
                  <select
                    className="form-select form-select-white min-h-40px"
                    value={orderTypeFilter}
                    onChange={(event: any) => {
                      onOrderTypeFilterChange(event.target.value);
                    }}
                  >
                    <option value="all">All orders</option>
                    <option value="pharmacy">Pharmacy</option>
                    <option value="utrade">Utrade</option>
                  </select>
                </div>
                <div className="position-relative flex-grow-1 max-w-322px d-flex align-items-center">
                  <KTSVG
                    path="/media/icons/duotune/general/gen021.svg"
                    className="svg-icon-3 position-absolute ms-3"
                  />
                  <input
                    type="text"
                    id="kt_filter_search"
                    className="form-control form-control-white min-h-40px min-w-200px form-control-lg ps-10"
                    placeholder="Search by order id…"
                    value={searchTerm}
                    onChange={(event: any) => {
                      onSearchChange(event.target.value);
                    }}
                  />
                </div>
              </div>
            </Col>
          </div>
          <div className="text-end">
            <Button className="uw-btn uw-btn-primary" onClick={onClose}>{'Close'}</Button>
          </div>
        </div>
      </Modal.Header>
      {loading ? (
        <div className="w-100 d-flex justify-content-center mt-6">
          <Loader />
        </div>
      ) : orders && orders.length > 0 ? (
        <Modal.Body>
          {!visible ? (
            <></>
          ) : (
            <>
              {discardedOrder && discardedOrder.length > 0 ? (
                <>
                  {discardedOrder.length ? (
                    <div>
                      <Notification
                        bgColor="bg-light-danger"
                        textColor="dark"
                        text={`You have selected ${selectedOrder} orders, but ${discardedOrder.length} order(s) were omitted due to either vehicle capacity or the time duration needed to deliver the order, which will not fit in any truck.`}
                        onClick={() => {
                          onToggleDiscarded();
                        }}
                        click={true}
                        clickText={!discarded ? 'Show Discarded Orders' : 'Show Planned Orders'}
                      />
                      <br />
                      <div className="d-flex flex-row">
                        <div className="d-flex align-items-center">
                          {getTotalTravelDuration(modalVehicleAnalytics?.orders) >
                          toSafeNumber(modalVehicleAnalytics?.vehicleDurationLimit) ? (
                            <span className="badge badge-danger fs-15 fw-600 text-white p-3 border-r4px me-5">
                              Travel Timedd:{' '}
                              {formatDurationSafe(
                                getTotalTravelDuration(modalVehicleAnalytics?.orders),
                              )}
                              /{' '}
                              {formatDurationSafe(
                                modalVehicleAnalytics?.vehicleDurationLimit,
                              )}
                            </span>
                          ) : (
                            <span className="badge badge-light-warning fs-15 fw-600 text-dark p-3 border-r4px me-5">
                              Travel Time:{' '}
                              {formatDurationSafe(
                                getTotalTravelDuration(modalVehicleAnalytics?.orders),
                              )}
                              /
                              {formatDurationSafe(
                                modalVehicleAnalytics?.vehicleDurationLimit,
                              )}
                            </span>
                          )}
                        </div>
                        <div className="d-flex align-items-center">
                          <span className="badge badge-light-warning fs-15 fw-600 text-dark p-3 border-r4px me-5">
                            Vehicle Capacity:{' '}
                            {formatWeightTons(modalVehicleAnalytics?.consumedWeight)}/
                            {kgToTons(modalVehicleAnalytics?.vehicleCapacity ?? 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                </>
              ) : (
                <></>
              )}
            </>
          )}

          <br />
          {discarded && discardedOrder.length ? (
            <DiscardedOrdersTable
              discardedOrder={discardedOrder}
              currentUser={currentUser}
              selectLoading={selectLoading}
            />
          ) : (
            <EditableOrdersTable
              orders={orders}
              filteredOrders={filteredOrders}
              selectedId={selectedId}
              totalRecords={totalRecords}
              selectLoading={selectLoading}
              currentUser={currentUser}
              onSelectAll={onSelectAll}
              onChecked={onChecked}
            />
          )}

          <Modal.Footer className="border-top-0">
            <Button className="uw-btn uw-btn-primary uw-btn-lg"
              variant="primary"
              size="lg"
              onClick={onSave}
              disabled={!selectedId.length}
            >
              {!onSaveLoader && <span className="indicator-label">Save</span>}
              {onSaveLoader && (
                <span className="indicator-progress" style={{ display: 'block' }}>
                  Please wait...
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              )}
            </Button>
          </Modal.Footer>
        </Modal.Body>
      ) : (
        <div className="text-center">
          <p>No Order Found</p>
        </div>
      )}
    </Modal>
  );
};

export default EditRouteOrderModal;
